import { makeStyles, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import {
    areIntervalsOverlapping,
    compareAsc,
    eachDayOfInterval,
    eachMonthOfInterval,
    endOfDay,
    endOfMonth,
    endOfWeek,
    isSameMonth,
    isToday,
    startOfDay,
    startOfMonth,
    startOfWeek,
} from 'date-fns';
import React from 'react';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { Button } from '../forms/Button';
import { CalendarContext, CalendarEvent } from './Calendar';
import { Event, EventList } from './Event';

const useYearViewClasses = makeStyles({
    root: {
        display: 'flex',
        justifyContent: 'space-evenly',
        flexWrap: 'wrap',
        overflow: 'auto',
        '@supports (overflow: overlay)': { overflow: 'overlay' },
        flex: '1 0 0',
        gap: 32,
        padding: 32,
        '& > div': { flex: '0 1 20%' },
    },
});

/**
 * Calendar year view.
 *
 * @param props.context calendar context
 */
export const YearView = (props: { context: React.Context<CalendarContext> }) => {
    const classes = useYearViewClasses();
    const { events, intervals, onFilter } = React.useContext(props.context);
    const yearEvents = React.useMemo(
        () => events.filter(event => areIntervalsOverlapping(event.interval, intervals.year, { inclusive: true })),
        [events, +intervals.year.start, +intervals.year.end],
    );

    React.useLayoutEffect(() => onFilter?.(yearEvents), [onFilter, yearEvents]);

    return (
        <div className={clsx(classes.root, 'scroll-shadow')}>
            {eachMonthOfInterval(intervals.year).map(month => (
                <Month key={+month} {...props} events={yearEvents} month={month} />
            ))}
        </div>
    );
};

export const useMonthClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    header: {
        gap: 10,
        padding: 8,
        alignItems: 'center',
        height: 40,
        color: theme.colors.neutral[1],
        backgroundColor: 'transparent',
        ...theme.fonts.h6,
        textTransform: 'uppercase',
        '&:hover': { filter: 'none', backgroundColor: theme.colors.neutral[4] },
    },
    tooltip: {
        padding: 8,
        color: theme.colors.neutral[1],
        backgroundColor: 'white',
        boxShadow: '0 0 16px 0 #2840533D',
        ...theme.fonts.caption,
    },
    days: {
        display: 'grid',
        gridAutoRows: 32,
        gridTemplateColumns: 'repeat(7, 32px)',
        gap: 8,
    },
    day: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: '50%',
        color: theme.colors.neutral[2],
        backgroundColor: 'transparent',
        ...theme.fonts.h6,
        '&:hover': { filter: 'none' },
        '&:not(.month)': {
            cursor: 'default',
            color: theme.colors.neutral[10],
        },
        '&.count': {
            color: theme.colors.neutral[2],
            backgroundColor: theme.colors.neutral[6],
        },
        '&.month.event': {
            color: theme.colors.blue[2],
            backgroundColor: theme.colors.blue[3],
        },
        '&.month.now': {
            color: 'white',
            backgroundColor: theme.colors.darkBlue[5],
        },
        '&.month:is(&.event,&.now):is(&.open,&:hover)': {
            color: 'white',
            backgroundColor: theme.colors.neutral[2],
        },
        '&.drag-over': { boxShadow: `0 0 4px 2px ${theme.colors.neutral[3]} inset` },
    },
});

/**
 * Render a month section of the year view.
 *
 * @param props.context calendar context
 * @param props.events events to render
 * @param props.month date inside the month to be rendered
 */
const Month = (props: { context: React.Context<CalendarContext>; events: CalendarEvent[]; month: Date }) => {
    const classes = useMonthClasses();
    const { t } = usePrefixTranslation('global', { global: 'calendar.YearView' });
    const { setView, setAnchor, onClick, onExternalDnD } = React.useContext(props.context);
    const interval = { start: startOfMonth(props.month), end: endOfMonth(props.month) };
    const weekInterval = { start: startOfWeek(interval.start), end: endOfWeek(interval.end) };
    const monthEvents = React.useMemo(
        () => props.events.filter(event => areIntervalsOverlapping(event.interval, interval, { inclusive: true })),
        [props.events, +interval.start, +interval.end],
    );

    const eventData = eachDayOfInterval(weekInterval).map(day => {
        const interval = { start: startOfDay(day), end: endOfDay(day) };
        const events = monthEvents
            .filter(e => areIntervalsOverlapping(e.interval, interval, { inclusive: true }))
            .sort(({ interval: a }, { interval: b }) => compareAsc(a.start, b.start))
            .map((e, i) => <Event key={i} event={e} view='year' tooltip onClick={() => onClick?.(e)} />);
        const eventList = events.length > 0 && (
            <EventList
                date={day}
                events={events}
                action={{ label: t('go-to-month'), onClick: () => (setView('month'), setAnchor(day)) }}
            />
        );
        return { day, events, eventList };
    });

    const days = eventData.map(({ day, events, eventList }) => {
        const cls = { month: isSameMonth(day, props.month), event: events.length > 0, now: isToday(day) };
        return (
            <Button
                key={+day}
                className={clsx(classes.day, cls)}
                icon='rounded'
                menu={eventList}
                menuProps={{
                    anchorOrigin: { horizontal: 'right', vertical: 'top' },
                    transformOrigin: { horizontal: 'left', vertical: 'top' },
                }}
                onDragEnter={e => onExternalDnD && e.currentTarget.classList.add('drag-over')}
                onDragOver={e => onExternalDnD && e.preventDefault()}
                onDragLeave={e => e.target === e.currentTarget && e.currentTarget.classList.remove('drag-over')}
                onDrop={e => (e.currentTarget.classList.remove('drag-over'), onExternalDnD?.(e, day))}
            >
                {day.getDate()}
            </Button>
        );
    });

    return (
        <div className={classes.root}>
            <Tooltip placement='right' classes={{ tooltip: classes.tooltip }} title={t('view-month')}>
                <span style={{ alignSelf: 'flex-start' }}>
                    <Button className={classes.header} onClick={() => (setView('month'), setAnchor(props.month))}>
                        {t('month', { date: props.month })}
                        {monthEvents.length > 0 && (
                            <div className={clsx(classes.day, 'count')}>{monthEvents.length}</div>
                        )}
                    </Button>
                </span>
            </Tooltip>
            <div className={classes.days}>{days}</div>
        </div>
    );
};
